import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { getEAProjects } from '../services/ea-api'
import type { EAProjectStatus, EAProjectPhase } from '../types/ea'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

const VENDOR_COLORS: Record<string, string> = {
  Arista: '#2563eb',
  PaloAlto: '#dc2626',
  Nutanix: '#059669',
  Azure: '#0284c7',
  Cisco: '#7c3aed',
}

const STATUS_COLORS: Record<string, string> = {
  active: '#10b981',
  on_hold: '#f59e0b',
  complete: '#3b82f6',
  cancelled: '#ef4444',
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  on_hold: 'On Hold',
  complete: 'Complete',
  cancelled: 'Cancelled',
}

const PHASE_LABELS: Record<string, string> = {
  discovery: 'Discovery',
  design: 'Design',
  standards_gate: 'Standards Gate',
  deployment: 'Deployment',
  qc: 'QC Validation',
  deliverables: 'Deliverables',
  complete: 'Complete',
}

const ALL_VENDORS = ['Arista', 'PaloAlto', 'Nutanix', 'Azure', 'Cisco']
const ALL_STATUSES: EAProjectStatus[] = ['active', 'on_hold', 'complete', 'cancelled']
const ALL_PHASES: EAProjectPhase[] = ['discovery', 'design', 'standards_gate', 'deployment', 'qc', 'deliverables', 'complete']

function StatusBadge({ status }: { status: EAProjectStatus }) {
  const color = STATUS_COLORS[status] || '#94a3b8'
  const label = STATUS_LABELS[status] || status
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full"
      style={{ background: `${color}22`, color, border: `1px solid ${color}44` }}
    >
      {label}
    </span>
  )
}

function PhaseBadge({ phase }: { phase: EAProjectPhase }) {
  const label = PHASE_LABELS[phase] || phase
  return (
    <span
      className="px-2.5 py-1 text-xs font-medium rounded-full"
      style={{ background: 'rgba(139, 92, 246, 0.15)', color: '#a78bfa', border: '1px solid rgba(139, 92, 246, 0.3)' }}
    >
      {label}
    </span>
  )
}

function VendorBadge({ vendor }: { vendor: string }) {
  const color = VENDOR_COLORS[vendor] || '#94a3b8'
  return (
    <span
      className="px-2 py-0.5 text-xs font-medium rounded-full text-white"
      style={{ background: color }}
    >
      {vendor}
    </span>
  )
}

export default function EAProjects() {
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [vendorFilter, setVendorFilter] = useState<string>('all')
  const [phaseFilter, setPhaseFilter] = useState<string>('all')
  const [sortBy, setSortBy] = useState('updated')

  const { data: projectsData, isLoading } = useQuery({
    queryKey: ['ea-projects'],
    queryFn: () => getEAProjects().then((res) => res.data),
  })

  const projects = Array.isArray(projectsData) ? projectsData : []

  const filteredProjects = useMemo(() => {
    let filtered = projects.filter(
      (p) =>
        p.project_name.toLowerCase().includes(search.toLowerCase()) ||
        p.client_name.toLowerCase().includes(search.toLowerCase()) ||
        p.project_code.toLowerCase().includes(search.toLowerCase())
    )

    if (statusFilter !== 'all') {
      filtered = filtered.filter((p) => p.status === statusFilter)
    }

    if (vendorFilter !== 'all') {
      filtered = filtered.filter((p) => p.vendors.includes(vendorFilter))
    }

    if (phaseFilter !== 'all') {
      filtered = filtered.filter((p) => p.current_phase === phaseFilter)
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.project_name.localeCompare(b.project_name)
        case 'client':
          return a.client_name.localeCompare(b.client_name)
        case 'created':
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        default:
          return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()
      }
    })

    return filtered
  }, [projects, search, statusFilter, vendorFilter, phaseFilter, sortBy])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className="w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '2px solid rgba(59, 130, 246, 0.3)', boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }}
          >
            <i className="fa-solid fa-spinner fa-spin text-2xl" style={{ color: '#60a5fa' }}></i>
          </div>
          <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading EA projects...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>EA Projects</h1>
          <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            Manage enterprise architecture engagements
          </p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="relative">
            <input
              type="text"
              placeholder="Search projects..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-80 pl-10 pr-4 py-2.5 rounded-xl text-sm"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
                color: '#e2e8f0',
              }}
            />
            <i className="fa-solid fa-search absolute left-3 top-3" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <Link
            to="/ea/projects/new"
            className="px-5 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 flex items-center space-x-2 hover:scale-105"
            style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
              border: '1px solid rgba(96, 165, 250, 0.4)',
              color: '#e2e8f0',
            }}
          >
            <i className="fa-solid fa-plus"></i>
            <span>New Project</span>
          </Link>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="rounded-xl p-4" style={glassPanel}>
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center space-x-3">
            <span className="text-sm font-medium" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Filter by:</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Status</option>
              {ALL_STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABELS[s]}</option>
              ))}
            </select>
            <select
              value={vendorFilter}
              onChange={(e) => setVendorFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Vendors</option>
              {ALL_VENDORS.map((v) => (
                <option key={v} value={v}>{v}</option>
              ))}
            </select>
            <select
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="all">All Phases</option>
              {ALL_PHASES.map((p) => (
                <option key={p} value={p}>{PHASE_LABELS[p]}</option>
              ))}
            </select>
          </div>
          <div className="flex items-center space-x-3">
            <span className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Sort by:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-4 py-2 rounded-lg text-sm"
              style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
            >
              <option value="updated">Last Updated</option>
              <option value="name">Project Name</option>
              <option value="client">Client Name</option>
              <option value="created">Date Created</option>
            </select>
          </div>
        </div>
      </div>

      {/* Project Cards */}
      {filteredProjects.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjects.map((project) => (
            <Link
              key={project.id}
              to={`/ea/projects/${project.id}`}
              className="rounded-xl transition-all duration-300 hover:scale-[1.02] group cursor-pointer"
              style={{
                background: 'rgba(30, 41, 59, 0.5)',
                border: '1px solid rgba(100, 180, 255, 0.15)',
              }}
            >
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono mb-1" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      {project.project_code}
                    </p>
                    <h3 className="text-lg font-bold truncate group-hover:text-blue-400 transition-colors" style={{ color: '#e2e8f0' }}>
                      {project.project_name}
                    </h3>
                  </div>
                  <StatusBadge status={project.status} />
                </div>

                <p className="text-sm mb-3" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                  {project.client_name}
                </p>

                <div className="flex flex-wrap gap-1.5 mb-4">
                  {project.vendors.map((v) => (
                    <VendorBadge key={v} vendor={v} />
                  ))}
                </div>

                <div
                  className="flex items-center justify-between pt-4"
                  style={{ borderTop: '1px solid rgba(100, 180, 255, 0.1)' }}
                >
                  <PhaseBadge phase={project.current_phase} />
                  <div className="flex items-center space-x-3 text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                    {project.project_lead && (
                      <span className="flex items-center gap-1">
                        <i className="fa-solid fa-user"></i>
                        {project.project_lead}
                      </span>
                    )}
                    <span>{new Date(project.updated_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div
            className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(59, 130, 246, 0.1)', border: '1px solid rgba(59, 130, 246, 0.2)' }}
          >
            <i className="fa-solid fa-diagram-project text-3xl" style={{ color: 'rgba(96, 165, 250, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>
            {search || statusFilter !== 'all' || vendorFilter !== 'all' || phaseFilter !== 'all'
              ? 'No projects match your filters'
              : 'No EA projects yet'}
          </h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
            {search || statusFilter !== 'all' || vendorFilter !== 'all' || phaseFilter !== 'all'
              ? 'Try adjusting your search or filter criteria'
              : 'Get started by creating your first EA project'}
          </p>
          {!search && statusFilter === 'all' && vendorFilter === 'all' && phaseFilter === 'all' && (
            <Link
              to="/ea/projects/new"
              className="px-6 py-3 rounded-xl text-sm font-medium transition-all duration-200 inline-flex items-center space-x-2 hover:scale-105"
              style={{
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)',
                border: '1px solid rgba(96, 165, 250, 0.4)',
                color: '#e2e8f0',
              }}
            >
              <i className="fa-solid fa-plus"></i>
              <span>Create your first EA project</span>
            </Link>
          )}
        </div>
      )}
    </div>
  )
}
